import React, { useState } from 'react';
import { AlertCircle, Calendar, TrendingUp, Package, Target, Activity, ChevronRight, ShoppingCart, FileSpreadsheet } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useApi } from '../hooks/useApi';

function OrderBuilder() {
  const { axiosPost } = useApi();
  // Get tomorrow's date as default
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split('T')[0];

  const [date, setDate] = useState(tomorrowStr);
  const [strategy, setStrategy] = useState('');
  const [baseAction, setBaseAction] = useState('');
  const [baseVolume, setBaseVolume] = useState(0);
  const [fixingVolume, setFixingVolume] = useState(50);
  const [fixingIMargin, setFixingIMargin] = useState(300);
  const [fixIModel, setFixIModel] = useState('v2.0.0');
  const [fixIIModel, setFixIIModel] = useState('v1.0.0-fix2');
  const [bucket1Spread, setBucket1Spread] = useState(5);
  const [bucket2Spread, setBucket2Spread] = useState(10);
  const [bucket3Spread, setBucket3Spread] = useState(15);
  const [bucket1Volume, setBucket1Volume] = useState(20);
  const [bucket2Volume, setBucket2Volume] = useState(50);
  const [bucket3Volume, setBucket3Volume] = useState(100);
  const [predsDiffCutoff, setPredsDiffCutoff] = useState(5);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [orders, setOrders] = useState(null);
  const [displayHours1to24, setDisplayHours1to24] = useState(true);

  // Validation: check form validity
  const isValidForm = (() => {
    // Must select a strategy
    if (!strategy || strategy === '') {
      return false;
    }
    // Must select a valid action
    if (!baseAction || baseAction === '') {
      return false;
    }
    // If base action is Buy or Sell, base volume must be greater than 0
    if ((baseAction === 'Buy' || baseAction === 'Sell') && baseVolume <= 0) {
      return false;
    }
    return true;
  })();

  const exportToExcel = () => {
    if (!orders || !date) return;

    // Create a new workbook
    const wb = XLSX.utils.book_new();
    
    // Create worksheet data
    const wsData = [];
    
    // Add headers in row 2 (index 1)
    wsData[0] = []; // Empty row 1
    wsData[1] = ['', '', 'Ilość K', 'Limit K', 'Limit S', 'Ilość S']; // Row 2
    
    // Add hours 1-24 in column B (rows 3-26)
    for (let i = 1; i <= 24; i++) {
      wsData[i + 1] = ['']; // Initialize each row with empty first column
      wsData[i + 1][1] = i; // Column B (index 1)
    }
    
    // Populate data from orders
    orders.forEach((order) => {
      const rowIndex = order.hour + 2; // +2 because array is 0-indexed and we start at row 3
      if (!wsData[rowIndex]) {
        wsData[rowIndex] = ['', order.hour + 1]; // Initialize with hour in column B
      }
      
      if (order.action === 'Buy') {
        wsData[rowIndex][2] = order.volume; // Column C - Ilość K
        wsData[rowIndex][3] = Math.round(order.price);  // Column D - Limit K (rounded)
      } else if (order.action === 'Sell') {
        wsData[rowIndex][5] = order.volume; // Column F - Ilość S
        wsData[rowIndex][4] = Math.round(order.price);  // Column E - Limit S (rounded)
      }
    });
    
    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    
    // Set column widths
    ws['!cols'] = [
      { wch: 5 },   // Column A
      { wch: 5 },   // Column B
      { wch: 10 },  // Column C
      { wch: 10 },  // Column D
      { wch: 10 },  // Column E
      { wch: 10 }   // Column F
    ];
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, date);
    
    // Generate filename
    const filename = `zlecenia-fixI-na-${date}.xlsx`;
    
    // Write file
    XLSX.writeFile(wb, filename);
  };

  const handleBuildOrder = async () => {
    setLoading(true);
    setError('');
    setOrders(null);

    try {
      // Calculate base_volume_bought_sold based on action and volume
      const baseVolumeBoughtSold = baseAction === 'Buy' ? baseVolume : baseAction === 'Sell' ? -baseVolume : 0;

      let response;

      if (strategy === 'Step Models') {
        // Use Step Models endpoint with bucket configuration
        response = await axiosPost('/build-step-orders', {
          date: date,
          base_action: baseAction,
          base_volume: baseVolume,
          base_volume_bought_sold: baseVolumeBoughtSold,
          fixing_i_margin: fixingIMargin,
          fix_i_model: fixIModel,
          fix_ii_model: fixIIModel,
          bucket1_spread: bucket1Spread,
          bucket2_spread: bucket2Spread,
          bucket3_spread: bucket3Spread,
          bucket1_volume: bucket1Volume,
          bucket2_volume: bucket2Volume,
          bucket3_volume: bucket3Volume,
          preds_diff_cutoff: predsDiffCutoff
        });
      } else {
        // Use Combined Models endpoint (original)
        response = await axiosPost('/build-orders', {
          date: date,
          base_action: baseAction,
          base_volume: baseVolume,
          base_volume_bought_sold: baseVolumeBoughtSold,
          fixing_volume: fixingVolume,
          fixing_i_margin: fixingIMargin,
          fix_i_model: fixIModel,
          fix_ii_model: fixIIModel,
          preds_diff_cutoff: predsDiffCutoff
        });
      }

      if (response.data.status === 'success') {
        setOrders(response.data.orders);
      } else {
        setError(response.data.message || 'Failed to build orders');
      }
    } catch (err) {
      console.error('Build order error:', err);
      setError(err.message || 'Failed to build orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-[1600px] mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Order Builder</h1>
          <p className="text-gray-600">Build trading orders for Fix I based on predictions and market parameters</p>
        </div>

        {/* Parameters Form */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Date */}
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <Calendar size={16} />
                  <span>Date</span>
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Strategy */}
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <Activity size={16} />
                  <span>Strategy</span>
              </label>
              <select
                value={strategy}
                onChange={(e) => setStrategy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select strategy...</option>
                <option value="Combined Models">Combined Models</option>
                <option value="Step Models">Step Models</option>
              </select>
            </div>

            {/* Base Action */}
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <ShoppingCart size={16} />
                  <span>Base Action</span>
                </label>
              <select
                value={baseAction}
                onChange={(e) => {
                  setBaseAction(e.target.value);
                  // Reset base volume to 0 when switching to Idle
                  if (e.target.value === 'Idle') {
                    setBaseVolume(0);
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select action...</option>
                <option value="Buy">Buy</option>
                <option value="Idle">Idle</option>
                <option value="Sell">Sell</option>
              </select>
            </div>

            {/* Base Volume */}
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <Package size={16} />
                  <span>Base Volume</span>
              </label>
              <input
                type="number"
                value={baseVolume}
                onChange={(e) => setBaseVolume(parseFloat(e.target.value) || 0)}
                disabled={baseAction === 'Idle' || baseAction === ''}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  baseAction === 'Idle' || baseAction === '' ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
              />
            </div>

            {/* Fixing Volume */}
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <Package size={16} />
                  <span>Fixing Volume</span>
              </label>
              <input
                type="number"
                value={strategy === 'Step Models' ? 0 : fixingVolume}
                onChange={(e) => setFixingVolume(parseFloat(e.target.value) || 0)}
                disabled={strategy === 'Step Models'}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  strategy === 'Step Models' ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
              />
            </div>

            {/* Fixing I Margin */}
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <Target size={16} />
                  <span>Fixing I Margin</span>
              </label>
              <input
                type="number"
                value={fixingIMargin}
                onChange={(e) => setFixingIMargin(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Fix I Model */}
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <TrendingUp size={16}  />
                  <span>Fix I Model</span>
                </label>
              <select
                value={fixIModel}
                onChange={(e) => setFixIModel(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="v1.0.0">v1.0.0</option>
                <option value="v2.0.0">v2.0.0</option>
              </select>
            </div>

            {/* Fix II Model */}
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <TrendingUp size={16} />
                  <span>Fix II Model</span>
              </label>
              <select
                value={fixIIModel}
                onChange={(e) => setFixIIModel(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="v1.0.0-fix2">v1.0.0-fix2</option>
                <option value="v2.0.0-l1-fix2">v2.0.0-l1-fix2</option>
              </select>
            </div>

            {/* Preds Diff Cutoff */}
            <div>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <AlertCircle size={16} />
                <span>Preds Diff Cutoff (%)</span>
              </label>
              <input
                type="number"
                step="0.1"
                value={predsDiffCutoff}
                onChange={(e) => setPredsDiffCutoff(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Bucket Configuration - Only shown when Step Models is selected */}
          {strategy === 'Step Models' && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Bucket Configuration (Arbitrage)</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                    <Target size={16} />
                    <span>Bucket 1 Spread (PLN)</span>
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={bucket1Spread}
                    onChange={(e) => setBucket1Spread(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                    <Target size={16} />
                    <span>Bucket 2 Spread (PLN)</span>
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={bucket2Spread}
                    onChange={(e) => setBucket2Spread(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                    <Target size={16} />
                    <span>Bucket 3 Spread (PLN)</span>
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={bucket3Spread}
                    onChange={(e) => setBucket3Spread(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                    <Package size={16} />
                    <span>Bucket 1 Volume (MWh)</span>
                  </label>
                  <input
                    type="number"
                    step="1"
                    value={bucket1Volume}
                    onChange={(e) => setBucket1Volume(parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                    <Package size={16} />
                    <span>Bucket 2 Volume (MWh)</span>
                  </label>
                  <input
                    type="number"
                    step="1"
                    value={bucket2Volume}
                    onChange={(e) => setBucket2Volume(parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                    <Package size={16} />
                    <span>Bucket 3 Volume (MWh)</span>
                  </label>
                  <input
                    type="number"
                    step="1"
                    value={bucket3Volume}
                    onChange={(e) => setBucket3Volume(parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Run Button */}
          <div className="mt-6">
            <button
              onClick={handleBuildOrder}
              disabled={loading || !isValidForm}
              className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>Building Orders...</span>
                </>
              ) : (
                <>
                  <ChevronRight className="w-5 h-5" />
                  <span>Build Order</span>
                </>
              )}
            </button>
          </div>

          {/* Hour display format toggle */}
          <div className="mt-4 flex items-center space-x-3">
            <span className="text-sm text-gray-700">Display hours as 1-24</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={displayHours1to24}
                onChange={(e) => setDisplayHours1to24(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 rounded-xl p-4 flex items-start gap-3">
            <div className="mt-0.5">
              <AlertCircle className="h-5 w-5 text-red-500" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Results */}
        {orders && (
          <div className="bg-white rounded-xl shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                Fix I Orders for {date}
              </h2>
              <button
                onClick={exportToExcel}
                className="px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 flex items-center gap-2 transition-colors"
              >
                <FileSpreadsheet className="w-4 h-4" />
                Export to Excel
              </button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                      Hour
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">
                      Fix I Pred
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">
                      Fix II Pred
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">
                      Action
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">
                      Volume
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">
                      Order Price (PLN/MWh)
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {orders.map((order, index) => (
                    <tr key={index} className={
                      order.skipped
                        ? 'bg-gray-200'
                        : order.action !== 'None'
                          ? 'bg-white hover:bg-gray-50'
                          : 'hover:bg-gray-50'
                    }>
                      <td className="px-6 py-3 text-sm text-gray-900">
                        {String(displayHours1to24 ? order.hour + 1 : order.hour).padStart(2, '0')}:00
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-900 text-right">
                        {order.fix_i_pred ? order.fix_i_pred.toFixed(2) : '-'}
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-900 text-right">
                        {order.fix_ii_pred ? order.fix_ii_pred.toFixed(2) : '-'}
                      </td>
                      <td className="px-6 py-3 text-sm text-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          order.action === 'Buy'
                            ? 'bg-green-100 text-green-800'
                            : order.action === 'Sell'
                            ? 'bg-red-100 text-red-800'
                            : order.action === 'Idle'
                            ? 'bg-gray-300 text-gray-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {order.action.toLowerCase()}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-900 text-right">
                        {Math.round(order.volume)}
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-900 text-right">
                        {order.price.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default OrderBuilder;
